/**
 * Visual Tag 2.0 Service
 * 
 * Handles asset identification using vector embeddings and geo-filtering
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

export interface VisualTagIdentifyRequest {
	scanPhotoUrl: string;
	gpsLatitude: number;
	gpsLongitude: number;
	gpsAccuracy?: number;
	geoFilterRadiusMeters?: number;
	topCandidates?: number;
	locationId?: number;
}

export interface MatchedAsset {
	assetId: number;
	assetTag: string;
	assetType: string;
	similarityScore: number;
	matchConfidence: 'PERFECT' | 'HIGH' | 'MEDIUM' | 'LOW' | 'FAILED';
	distanceMeters: number;
}

export interface CandidateMatch {
	assetId: number;
	vectorId: number;
	similarityScore: number;
	distanceMeters: number;
	assetTag: string;
	assetType: string;
}

export interface VisualTagIdentifyResponse {
	success: boolean;
	matchFound: boolean;
	matchedAsset?: MatchedAsset;
	candidates: CandidateMatch[];
	candidateCount: number;
	geoFilterRadiusMeters: number;
	imageQualityScore: number;
	imageQualityPassed: boolean;
	driftUpdateTriggered: boolean;
	driftUpdateReason: string;
	identificationLogId: number;
}

/**
 * Identify an asset from a scan photo using Visual Tag 2.0
 */
export async function identifyAssetVisualTag(
	request: VisualTagIdentifyRequest
): Promise<VisualTagIdentifyResponse> {
	const user = auth.currentUser;
	if (!user) {
		throw new Error('User not authenticated');
	}

	const token = await user.getIdToken();

	const response = await fetch(`${VITE_CORE_API_URL}${CORE_URL}/visual-tag/identify`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify(request),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Failed to identify asset');
	}

	const result = await response.json();
	return result.data;
}

/**
 * Submit feedback on an identification result
 */
export async function submitVisualTagFeedback(
	identificationLogId: number,
	wasCorrect: boolean,
	correctedAssetId?: number,
	notes?: string
): Promise<void> {
	const user = auth.currentUser;
	if (!user) {
		throw new Error('User not authenticated');
	}

	const token = await user.getIdToken();

	const response = await fetch(`${VITE_CORE_API_URL}${CORE_URL}/visual-tag/feedback`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify({
			identificationLogId,
			wasCorrect,
			correctedAssetId,
			notes,
		}),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Failed to submit feedback');
	}
}

/**
 * Get enrollment statistics for the organization
 */
export async function getEnrollmentStats(): Promise<any> {
	const user = auth.currentUser;
	if (!user) {
		throw new Error('User not authenticated');
	}

	const token = await user.getIdToken();

	const response = await fetch(`${VITE_CORE_API_URL}${CORE_URL}/visual-tag/stats/enrollment`, {
		method: 'GET',
		headers: {
			'Authorization': `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.error || 'Failed to get enrollment stats');
	}

	const result = await response.json();
	return result.data;
}

